'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/utils/utils';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

// 添加 ErrorBoundary 组件
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.log('Error in SubmitForm:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong with the form.</h1>;
    }

    return this.props.children;
  }
}

const FormSchema = z.object({
  website: z.string(),
  url: z.string().url(),
  category: z.string(),
});

export default function SubmitForm({ className, locale }: { className?: string, locale: string }) {
  console.log('SubmitForm component rendered'); // 新增日志

  const t = useTranslations('Submit');
  const supabase = createClientComponentClient();
  const [loading, setLoading] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [categoryList, setCategoryList] = useState<any[]>([]);
  const [isCategoryLoading, setIsCategoryLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] = useState<z.infer<typeof FormSchema> | null>(null);

  const router = useRouter();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      website: '',
      url: '',
      category: '',
    },
  });

  const fetchCategories = useCallback(async (locale: string) => {
    setIsCategoryLoading(true);
    console.log('fetchCategories function called'); // 新增日志
    console.log('Fetching categories for locale:', locale);
    try {
      const { data, error } = await supabase
        .from('category_intl')
        .select('id, category_name, category_id')
        .eq('locale', locale);

      if (error) throw error;

      console.log('Fetched categories:', data);
      setCategoryList(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error(t('Failed to load categories'));
    } finally {
      setIsCategoryLoading(false);
    }
  }, [supabase, t]);

  useEffect(() => {
    console.log('SubmitForm useEffect triggered'); // 新增日志
    console.log('Current locale:', locale); // 新增日志
    setIsClient(true);
    fetchCategories(locale);
  }, [fetchCategories, locale]);

  const openConfirmDialog = (formData: z.infer<typeof FormSchema>) => {
    setFormDataToSubmit(formData);
    setIsOpen(true);
  };

  const closeConfirmDialog = () => {
    setIsOpen(false);
    setFormDataToSubmit(null);
  };

  const handleConfirm = async () => {
    if (!formDataToSubmit) return;
    
    closeConfirmDialog();
    try {
      setLoading(true);
      
      const response = await fetch('/api/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formDataToSubmit),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Submit failed');
      }

      toast.success(t('Submit success'));
      form.reset();
    } catch (error) {
      toast.error(error.message || t('Submit failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    const textToCopy = '<a href="https://AIToolly.com/" title="AIToolly AI Tools Directory">AIToolly</a>';
    navigator.clipboard.writeText(textToCopy).then(() => {
      toast.success(t('Copied to clipboard!'));
    }).catch(err => {
      toast.error(t('Failed to copy!'));
    });
  };

  if (!isClient) {
    return null;
  }

  return (
    <ErrorBoundary>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(openConfirmDialog)} className={cn('mx-auto max-w-md space-y-6 rounded-lg bg-white p-6 shadow-md', className)}>
          <div className='space-y-4'>
            <FormField
              control={form.control}
              name='website'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">{t('Tool Name')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='AIToolly'
                      className='h-10 rounded-md border border-gray-300 bg-white px-3'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs text-red-500" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='url'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">{t('Url')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://aitoolly.com"
                      className='h-10 rounded-md border border-gray-300 bg-white px-3'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs text-red-500" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='category'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">{t('Category')}</FormLabel>
                  <FormControl>
                    <select
                      className='h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900'
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isCategoryLoading}
                    >
                      <option value="">{isCategoryLoading ? t('Loading') : t('Select a category')}</option>
                      {!isCategoryLoading && categoryList.map((category) => (
                        <option key={category.id} value={category.category_id}>
                          {category.category_name}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage className="text-xs text-red-500" />
                </FormItem>
              )}
            />
          </div>
          
          <button
            type='submit'
            disabled={loading}
            className={cn(
              'w-full rounded-md bg-blue-500 py-2 text-white transition-colors hover:bg-blue-600',
              loading && 'cursor-not-allowed opacity-50'
            )}
          >
            {loading ? <span className="loading loading-spinner text-white"></span> : t('Submit')}
          </button>

          <div className="mt-6 space-y-4 text-center">
            <p className="text-sm text-gray-600">{t('Add this code to your homepage')}</p>
            <code
              className="block cursor-pointer rounded bg-gray-100 p-2 text-sm text-blue-600"
              onClick={handleCopy}
            >
              &lt;a href=&quot;https://AIToolly.com/&quot; title=&quot;Best AI Tools Directory&quot;&gt;AIToolly&lt;/a&gt;
            </code>
            <p className="text-sm text-gray-600">{t('Then submit your website for free')}</p>
            <p className="text-sm text-gray-600">{t('Or')}</p>
            <a href="/pricing" className="inline-block px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors duration-300">
              {t('Get Submit Pack')}
            </a>
          </div>
        </form>
      </Form>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeConfirmDialog}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {t('Confirm Submission')}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {t('Add AIToolly Confirm')}
                    </p>
                  </div>

                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={handleConfirm}
                    >
                      {t('Confirm')}
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                      onClick={closeConfirmDialog}
                    >
                      {t('Cancel')}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </ErrorBoundary>
  );
}